import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  Alert,
  Button
} from '@mui/material';
import { Security, Delete, Visibility, Lock, Home } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useLanguageContext } from '../contexts/LanguageContext';

const PrivacyPageEN: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguageContext();

  const handleGoHome = () => {
    navigate('/');
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 2,
          backgroundColor: 'transparent',
          color: 'white',
          '& .MuiTypography-root': { color: 'white' },
          '& .MuiAlert-root .MuiTypography-root': { color: 'inherit' },
          '& .MuiDivider-root': { borderColor: 'rgba(255, 255, 255, 0.3)' }
        }}
      >
        <Typography variant="h3" component="h1" gutterBottom align="center" color="primary">
          Privacy Policy
        </Typography>
        
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 4 }}>
          Effective Date: January 10, 2025
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Button
            variant="outlined"
            startIcon={<Home />}
            onClick={handleGoHome}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              borderColor: 'white',
              color: 'white',
              '&:hover': {
                backgroundColor: 'white',
                color: '#6F4E37',
                borderColor: 'white'
              }
            }}
          >
            {t('privacy.goHome')}
          </Button>
        </Box>

        <Alert severity="info" icon={<Lock />} sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            🔒 Privacy Protection Notice
          </Typography>
          <Typography variant="body2" component="div">
            • No login is required to protect the privacy of prayer content.<br/>
            • Gender information is used solely for worship preparation (Bible verses, benedictions, etc.).<br/>
            • All information is automatically deleted after the worship service ends.
          </Typography>
        </Alert>

        <Divider sx={{ mb: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            Article 1 (Purpose of Personal Information Processing)
          </Typography>
          <Typography variant="body1" paragraph>
            Advanced Society Initiative (the "Company") processes personal information for the following purposes. The personal information being processed shall not be used for purposes other than the following. If the purpose of use changes, necessary measures such as obtaining separate consent in accordance with Article 18 of the Personal Information Protection Act will be implemented.
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="bold">
              YouTube API Services Notice: This service uses YouTube API Services to provide hymn search and playback functionality. YouTube-related data processing follows the Google Privacy Policy (http://www.google.com/policies/privacy).
            </Typography>
          </Alert>
          <List>
            <ListItem>
              <Security sx={{ mr: 2, color: 'primary.main' }} />
              <ListItemText 
                primary="Providing Worship Services"
                secondary="Collecting minimal information to personalize worship, including gender-appropriate Bible verses and benedictions"
              />
            </ListItem>
            <ListItem>
              <Visibility sx={{ mr: 2, color: 'primary.main' }} />
              <ListItemText 
                primary="Service Improvement"
                secondary="Improving service quality through anonymized statistical information"
              />
            </ListItem>
          </List>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            Article 2 (Personal Information Processing and Retention Period)
          </Typography>
          <Alert severity="success" icon={<Delete />} sx={{ mb: 2 }}>
            <Typography variant="body1" fontWeight="bold">
              Temporary Processing Principle: All personal information is automatically deleted immediately upon the end of worship.
            </Typography>
          </Alert>
          <Typography variant="body1" paragraph>
            1. The Company processes and retains personal information within the retention and use period prescribed by law or consented to by the data subject at the time of collection.
          </Typography>
          <Typography variant="body1" paragraph>
            2. Due to the nature of this service, the following processing applies:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Gender Information"
                secondary="Used temporarily only during worship preparation and immediately deleted"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Prayer Content"
                secondary="Not stored on the server and completely deleted when the browser session ends"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Worship-related Settings"
                secondary="Automatically deleted after worship ends"
              />
            </ListItem>
          </List>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            Article 3 (Items of Personal Information Processed)
          </Typography>
          <Typography variant="body1" paragraph>
            The Company collects only minimal personal information:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Optional Information"
                secondary="Gender (for personalizing Bible verses and benedictions for worship preparation)"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Automatically Collected Information"
                secondary="Service usage records, access logs (for statistical purposes, non-personally identifiable). Cookies may be used in a minimal scope for service quality and security."
              />
            </ListItem>
          </List>
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              Note: We do not, in principle, collect personally identifiable information such as names, phone numbers, or email addresses. Information collected by external payment processors during payment follows their policies.
            </Typography>
          </Alert>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            Article 4 (Provision of Personal Information to Third Parties)
          </Typography>
          <Typography variant="body1" paragraph>
            The Company processes personal information of data subjects only within the scope specified in Article 1 (Purpose of Personal Information Processing) and provides personal information to third parties only when it falls under Articles 17 and 18 of the Personal Information Protection Act, such as with the data subject's consent or under special provisions of law.
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2" fontWeight="bold">
                The Company does not currently provide personal information to third parties. However, when using external services (e.g., payment processing, YouTube embed), limited information may be transmitted to the service providers as necessary, and their privacy policies will apply.
              </Typography>
          </Alert>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            Article 5 (Destruction of Personal Information)
          </Typography>
          <Typography variant="body1" paragraph>
            The Company destroys personal information without delay when it becomes unnecessary, such as when the retention period expires or the processing purpose is achieved.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Destruction Procedures and Methods:</strong>
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Automatic Destruction"
                secondary="All temporary data is automatically deleted by the system when worship ends"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Session Termination"
                secondary="All local data is automatically deleted when the browser session ends"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Irrecoverable"
                secondary="Deleted information is processed to be technically irrecoverable"
              />
            </ListItem>
          </List>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            Article 6 (Rights and Obligations of Data Subjects and Legal Representatives and Exercise Methods)
          </Typography>
          <Typography variant="body1" paragraph>
            Data subjects may exercise the following personal information protection-related rights against the Company at any time:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="1. Request for notification of personal information processing status"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="2. Request for correction or deletion in case of errors"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="3. Request for suspension of processing"
              />
            </ListItem>
          </List>
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              Since this service does not store personal information, most rights are automatically guaranteed.
            </Typography>
          </Alert>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            Article 7 (Personal Information Protection Officer)
          </Typography>
          <Typography variant="body1" paragraph>
            The Company has designated a Personal Information Protection Officer as follows to take overall responsibility for personal information processing-related work and to handle complaints and remedy damages related to personal information processing by data subjects.
          </Typography>
          <Paper elevation={0} sx={{ bgcolor: 'transparent', border: '1px solid rgba(255, 255, 255, 0.4)', p: 2, mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Personal Information Protection Officer
            </Typography>
                            <Typography variant="body2">Name: Huney Kong</Typography>
            <Typography variant="body2">Position: CEO</Typography>
            <Typography variant="body2">Contact: 070-8621-3108</Typography>
            <Typography variant="body2">Address: 102, Building A, Samhyeong Park, 9-26 Pyeongchang 21-gil, Jongno-gu, Seoul</Typography>
          </Paper>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            Article 8 (Measures to Ensure Safety of Personal Information)
          </Typography>
          <Typography variant="body1" paragraph>
            The Company is taking the following measures to ensure the safety of personal information:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Technical Measures"
                secondary="Access authority management for personal information processing systems, access control systems, transport-layer encryption (HTTPS), encryption of unique identification information, etc."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Physical Measures"
                secondary="Access control to computer rooms, data storage rooms, etc."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Administrative Measures"
                secondary="Minimization and education of personal information handlers, periodic reviews"
              />
            </ListItem>
          </List>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom color="primary">
            Article 9 (Changes to Privacy Policy)
          </Typography>
          <Typography variant="body1" paragraph>
            1. This Privacy Policy applies from the effective date. When there are additions, deletions, or corrections due to changes in laws or policies, notice will be given through announcements starting 7 days before the effective date of the changes.
          </Typography>
          <Typography variant="body1" paragraph>
            2. This policy is effective from January 10, 2025.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button
            variant="contained"
            startIcon={<Home />}
            onClick={handleGoHome}
            sx={{
              mb: 3,
              borderRadius: 2,
              px: 4,
              py: 1.5,
              background: '#6F4E37',
              '&:hover': {
                background: '#5A3E2B',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            {t('privacy.goHome')}
          </Button>
          <Typography variant="body2" color="text.secondary">
            If you have any questions about privacy, please contact Advanced Society Initiative (huney.kong@gmail.com).
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default PrivacyPageEN;